import { computed, watch } from 'vue'

import type { RabbitEventsScoutsMessage } from '@collector/queue-gateway-shared-types'
import type { EventScout } from '@collector/sportsapi-client-legacy'
import { queryClient } from '@desktop/queryClient'

import { validateAssignedScouts } from '../utils/validation'
import { useEventScoutsQuery } from './queries/useEventScoutsQuery'
import { useQueueClient } from './queue/useQueueClient'
import { useEventId } from './useEventId'

/**
 * Scouts socket → refetch scouts + access check.
 * No sync option — same as today's eventScouts.ts.
 */
const scoutsHandler = async (
  eventId: number,
  messages: RabbitEventsScoutsMessage[],
) => {
  await Promise.all(
    messages.map(async (_) => {
      const queryKey = ['event', 'scouts', eventId] as const

      await queryClient.invalidateQueries({ queryKey, exact: true })
      const scouts = queryClient.getQueryData<EventScout[]>(queryKey) ?? []
      validateAssignedScouts(scouts)
    }),
  )
}

export const useEventScouts = () => {
  const { data } = useEventScoutsQuery()

  return {
    eventScouts: computed(() => data.value!),
  }
}

export const useLiveEventScouts = () => {
  const { connected, client } = useQueueClient()
  const eventId = useEventId()

  watch(
    connected,
    (isConnected, _, onCleanup) => {
      if (!isConnected) return
      onCleanup(
        client.subscribe(`events/${eventId.value}/events_scouts`, {
          onMessageBatch: (messages) => scoutsHandler(eventId.value, messages),
        }),
      )
    },
    { immediate: true },
  )
}
